import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, Collapse, Grid, Modal } from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import FilterWrapper from "../components/general/FiltersWrapper";
import Loader from "../components/general/Loader";
import LoadingBackdrop from "../components/general/LoadingBackdrop";
import NoData from "../components/general/NoData";
import NunitoText from "../components/general/NunitoText";
import ReusableButton from "../components/general/ReusableButton";
import ReusableTextField from "../components/general/ReusableTextField";
import DeleteModal from "../components/modal/DeleteModal";
import AddParticipantModal from "../components/Participant/AddParticipantModal";
import EditParticipantModal from "../components/Participant/EditParticipantModal";
import ExportParticipantModal from "../components/Participant/ExportParticipantModal";
import HeavyTable from "../components/table/HeavyTable";
import ViewAndFilterButton from "../components/ViewAndFilterButton";
import ViewOption from "../components/ViewOption";
import { showWarningSwal } from "../functions/alert";
import { isAdmin } from "../functions/checkrole";
import { deleteParticipant } from "../functions/deleteData";
import {
  buildQueryString,
  cancelAction,
  changeViewOptions,
  controlFilter,
  controlView,
  getDateDiff,
  makeSureIsArray,
} from "../functions/general";
import { enhancedGetParticipants } from "../functions/getData";
import { showNotification } from "../functions/snackbar";

const participantHeader = () =>
  [
    { id: "name", label: "Name" },
    { id: "index_no", label: "Index" },
    isAdmin() && { id: "country_name", label: "Country" },
    { id: "identifier", label: "Identifier" },
    { id: "is_answers_uploaded", label: "Answers Uploaded" },
    { id: "email", label: "email" },
    { id: "status", label: "Status" },
    { id: "privateCenter", label: "Private" },
    { id: "competition_name", label: "Competition/Assessment" },
    { id: "school_name", label: "School" },
    { id: "tuition_centre_name", label: "Tuition Centre" },
    { id: "grade", label: "Grade" },
    { id: "class", label: "Class" },
    { id: "certificate_no", label: "Certificate No" },
    { id: "award", label: "Award" },
    { id: "created_by", label: "Created By" },
    { id: "last_modified_by", label: "Last Modified By" },
  ].filter(Boolean);

export default function EnhancedParticipants({ setPartcipient }) {
  document.title = "Participant";
  const [header, setHeader] = useState(participantHeader());
  const [participantTable, setParticipantTable] = useState();
  const [viewOptions, setViewOptions] = useState(
    [
      isAdmin() && { label: "Country", state: true, key: "country_name" },
      { label: "Competition/Assessment", state: true, key: "competition_name" },
      { label: "School", state: true, key: "school_name" },
      { label: "Tuition Centre", state: true, key: "tuition_centre_name" },
      { label: "Grade", state: true, key: "grade" },
      { label: "Class", state: true, key: "class" },
      { label: "Partner", state: true, key: "partner_name" },
      { label: "Created By", state: true, key: "created_by" },
      { label: "Last Modified By", state: true, key: "last_modified_by" },
      { label: "Status", state: true, key: "status" },
    ].filter(Boolean)
  );
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);
  const [firstLoaded, setFirstLoaded] = useState(false);
  const [viewing, setViewing] = useState(false);
  const [filtering, setFiltering] = useState(false);
  const [id, setId] = useState("");
  const [selected, setSelected] = useState([]);
  const [selecting, setSelecting] = useState([]);
  const [onDelete, setOnDelete] = useState(false);
  const [onAddParticipant, setOnAddParticipant] = useState(false);
  const [onEditParticipant, setOnEditParticipant] = useState(false);
  const [onExport, setOnExport] = useState(false);
  const [sortAbleColumns, setSortableColumns] = useState([]);

  // Consolidated query parameters state
  const [queryParams, setQueryParams] = useState({
    limits: 10,
    page: 1,
    search: "",
    country: "",
    school: "",
    grade: "",
    status: "",
    competition: "",
    organization: "",
    private: "",
    orderBy: "",
    orderDirection: "",
  });

  const handleView = () => controlView(viewing, setViewing, setFiltering);
  const handleFilter = () => controlFilter(filtering, setFiltering, setViewing);
  const cancelDelete = () => cancelAction(setOnDelete, setId);
  const cancelEdit = () => cancelAction(setOnEditParticipant, setId);
  const cancelExport = () => cancelAction(setOnExport);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // Update query parameters
  const updateQueryParams = (updates) => {
    setQueryParams((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  // Reset query parameters to initial state
  const resetQueryParams = () => {
    setQueryParams({
      limits: 10,
      page: 1,
      search: "",
      country: "",
      school: "",
      grade: "",
      status: "",
      competition: "",
      organization: "",
      private: "",
      orderBy: "",
      orderDirection: "",
    });
  };

  const afterFunction = (cancel, data) => {
    if (data.status === 200) {
      resetQueryParams();
      setSearch("");
      fetchData();
      cancel();
      showNotification("success", data.message, enqueueSnackbar, closeSnackbar);
      setSelected([]);
      setSelecting([]);
    }
  };
  const isFirstRender = useRef(true);

  const deleteFunction = () => {
    deleteParticipant({ id: id === "" ? selected : [id] })
      .then((d) => {
        afterFunction(cancelDelete, d);
      })
      .catch((e) => console.log(e));
  };

  const adjustEndpoint = (endpoint, value) => {
    const currentParams = { ...queryParams };

    // Remove the current filter value if it's being changed
    if (value !== currentParams.country && currentParams.country) {
      endpoint += `&country=${currentParams.country}`;
    }
    if (value !== currentParams.school && currentParams.school) {
      endpoint += `&school=${currentParams.school}`;
    }
    if (value !== currentParams.grade && currentParams.grade) {
      endpoint += `&grade=${currentParams.grade}`;
    }
    if (value !== currentParams.status && currentParams.status) {
      endpoint += `&status=${currentParams.status}`;
    }
    if (value !== currentParams.organization && currentParams.organization) {
      endpoint += `&organization=${currentParams.organization}`;
    }
    if (value !== currentParams.competition && currentParams.competition) {
      endpoint += `&competition=${currentParams.competition}`;
    }
    if (value !== currentParams.private && currentParams.private) {
      endpoint += `&private=${currentParams.private}`;
    }

    return endpoint;
  };

  const url = "participant/list?get_filter=";
  const filters = [
    {
      label: "country",
      endpoint: `${url}country`,
      value: queryParams.country,
      onChange: (value) => updateQueryParams({ country: value }),
      key: "country",
      setValue: (value) => updateQueryParams({ country: value }),
    },
    {
      label: "school",
      endpoint: `${url}school`,
      value: queryParams.school,
      onChange: (value) => updateQueryParams({ school: value }),
      key: "school",
      setValue: (value) => updateQueryParams({ school: value }),
    },
    {
      label: "grade",
      endpoint: `${url}grade`,
      value: queryParams.grade,
      onChange: (value) => updateQueryParams({ grade: value }),
      key: "grade",
      setValue: (value) => updateQueryParams({ grade: value }),
    },
    {
      label: "Status",
      endpoint: `${url}status`,
      value: queryParams.status,
      onChange: (value) => updateQueryParams({ status: value }),
      key: "status",
      setValue: (value) => updateQueryParams({ status: value }),
    },
    {
      label: "competition",
      endpoint: `${url}competition`,
      value: queryParams.competition,
      onChange: (value) => updateQueryParams({ competition: value }),
      key: "competition",
      setValue: (value) => updateQueryParams({ competition: value }),
    },
    {
      label: "organization",
      endpoint: `${url}organization`,
      value: queryParams.organization,
      onChange: (value) => updateQueryParams({ organization: value }),
      key: "organization",
      setValue: (value) => updateQueryParams({ organization: value }),
    },
    {
      label: "private",
      endpoint: `${url}private`,
      value: queryParams.private,
      onChange: (value) => updateQueryParams({ private: value }),
      key: "private",
      setValue: (value) => updateQueryParams({ private: value }),
    },
  ];

  const formatTableData = (data) => {
    let formattedData = data.map((el, index) => {
      const answersUploaded = el?.is_answers_uploaded;
      const isUploaded =
        answersUploaded === true ||
        answersUploaded === 1 ||
        answersUploaded === "true";
      console.log(el);
      return {
        id: el?.id,
        name: el.name,
        competition_id: el.competition_organization?.competition_id,
        organization_name: el.competition_organization?.organization_name,
        country_id: el.country?.id,
        school_id: el.school_id,
        index_no: el.index_no,
        identifier: el.identifier,
        is_answers_uploaded: isUploaded ? "Yes" : "NO",
        country_name: el.country?.name,
        email: el.email,
        status: el.iac_status,
        privateCenter: el.tuition_centre_id === null ? "No" : "Yes",
        private: el.tuition_centre_id === null ? "No" : "Yes",
        competition_name: el.competition_organization?.competition.alias,
        school_name: el.school?.name,
        tuition_centre_name: el.tuition_centre?.name,
        tuition_centre_id: el?.tuition_centre_id || el.tuition_centre?.id,
        grade: el.grade,
        class: el.class,
        certificate_no: el.certificate_no,
        award: el.result?.award,
        created_by: el.created_by,
        last_modified_by: el.last_modified_by,
        competition_alias: el?.competition_organization?.competition.alias,
      };
    });
    return formattedData;
  };
  const fetchData = () => {
    setSelected([]);
    setSelecting([]);
    setLoading(true);

    const queryString = buildQueryString(queryParams);
    enhancedGetParticipants(queryString, controller.current?.signal).then(
      (res) => {
        res.data = formatTableData(res.data);
        setParticipantTable(res);
        setSortableColumns(res?.sortable_columns);
        setLoading(false);
      }
    );
  };

  const onChangeViewOptions = (value) =>
    changeViewOptions(participantHeader(), setViewOptions, setHeader, value);

  const handleSortingFunction = (sortingKey, direction) => {
    updateQueryParams({
      orderBy: sortingKey,
      orderDirection: direction,
    });
  };

  const onChangeSearch = (value) => {
    setSearch(value);
    updateQueryParams({ search: value });
  };

  const onChangePage = (pageNo) => {
    updateQueryParams({ page: pageNo });
  };

  const onChangeRowsPerPage = (numberOfRows) => {
    setRowsPerPage(numberOfRows);
    updateQueryParams({ limits: numberOfRows });
  };

  const controller = useRef(null);

  // Effect for handling URL changes and initial setup
  useEffect(() => {
    if (isFirstRender.current) isFirstRender.current = false;

    // Create a new abort controller for this effect
    controller.current = new AbortController();

    // Initial data fetch
    setSelected([]);
    setSelecting([]);
    setLoading(true);

    const queryString = buildQueryString(queryParams);
    enhancedGetParticipants(queryString, controller.current.signal).then(
      (res) => {
        res.data = formatTableData(res.data);
        setParticipantTable(res);
        setSortableColumns(res?.sortable_columns);
        setLoading(false);
      }
    );

    return () => {
      if (controller.current) {
        controller.current.abort();
      }
    };
  }, []);

  // Effect for data fetching when query parameters change
  useEffect(() => {
    if (controller.current && !isFirstRender.current) {
      setSelected([]);
      setSelecting([]);
      setLoading(true);

      const queryString = buildQueryString(queryParams);
      enhancedGetParticipants(queryString, controller.current.signal).then(
        (res) => {
          res.data = formatTableData(res.data);
          setParticipantTable(res);
          setSortableColumns(res?.sortable_columns);
          setLoading(false);
        }
      );
    }
  }, [queryParams]);

  const checkIfAllSelectedRowsCanBeDeleted = (selected, data) => {
    if (selected.length === 0) return false;
    for (let i = 0; i < selected.length; i++) {
      let row = makeSureIsArray(data).find((d) => d.id === selected[i]);
      if (getDateDiff(new Date(), new Date(row.created_at)) > 7) return false;
    }
    return true;
  };
  const onClickMassDelete = () => {
    if (!selected.length)
      return showWarningSwal(
        "Please select at least one participant to mass delete"
      );
    setOnDelete(true);
  };
  return (
    <Box className="wrapperBox">
      <LoadingBackdrop loading={loading} />
      <Collapse in={viewing}>
        <ViewOption
          viewOptions={viewOptions}
          rowsPerPage={rowsPerPage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          onChangeViewOptions={onChangeViewOptions}
        />
      </Collapse>
      <Collapse in={filtering}>
        <FilterWrapper filters={filters} adjustEndpoint={adjustEndpoint} />
      </Collapse>
      <Grid
        className="firstRowContainer"
        container
        justifyContent="space-between"
      >
        <div className="dashboardAndSelfBtnDiv">
          <ReusableButton
            text="Dashboard"
            fontSize={14}
            bgColor="#F16774"
            height={36}
            width={125}
            br={18}
            to="/dashboard"
            iconType="home"
          />
          <ChevronRightIcon />
          <ReusableButton
            text="Participants"
            fontSize={14}
            bgColor="#F16774"
            height={36}
            br={18}
          />
        </div>
        <div className="viewAndFilterBtnDiv">
          <ViewAndFilterButton
            text="View Options"
            state={viewing}
            fontSize={14}
            height={42}
            onClick={handleView}
            marginRight={10}
          />
          <ViewAndFilterButton
            text="Filter"
            state={filtering}
            fontSize={14}
            height={42}
            onClick={handleFilter}
          />
        </div>
      </Grid>
      <Grid className="tableContainer" container>
        <NunitoText
          value="Participants"
          fontSize={40}
          fontWeight={700}
          italic
          color="#144A94"
        />
        <Grid
          className="searchAndBtnContainer"
          container
          alignItems="center"
          justifyContent="space-between"
        >
          <ReusableTextField
            type="search"
            width={500}
            height={60}
            bgColor="#F2F2F2"
            placeholder="Search using keyword"
            state={search}
            setState={onChangeSearch}
            onBlur
          />
          <div>
            {/* <CSVButtons filename='participant-for-ocs' buttonText='Export CSV For OCS'
                            onClick={() => showWarningSwal('Select a participant to export')} data={csvDataForOCS()} />
                        <CSVButtons filename='participants' buttonText='Export CSV'
                            onClick={() => showWarningSwal('Select a participant to export')} data={csvData()} /> */}
            <ReusableButton
              text="Export CSV"
              fontSize={15}
              bgColor="#5E75C3"
              height={46}
              marginRight={20}
              iconType="export"
              onClick={() => setOnExport(true)}
            />
            <ReusableButton
              text="Import CSV"
              fontSize={15}
              bgColor="#5E75C3"
              height={46}
              marginRight={20}
              iconType="import"
              to="csvupload?upload=participant"
            />
          </div>
          <div className="endBtnDiv">
            <ReusableButton
              text="Add Participant"
              fontSize={15}
              bgColor="#5E75C3"
              height={48}
              marginRight={20}
              onClick={() => setOnAddParticipant(true)}
              iconType="add"
            />
            <ReusableButton
              text="Delete"
              fontSize={15}
              bgColor="#E83042"
              height={48}
              marginRight={20}
              onClick={() => onClickMassDelete()}
              iconType="delete"
            />
          </div>
        </Grid>
        <NunitoText
          value='Search only in "Name", "Index", "School" and "Tuition Centre" column'
          fontSize={20}
          fontWeight={400}
          italic
          color="#144A94"
        />
        {/* {JSON.stringify(forms)}
                {competitionOptions && countryOptions && schoolOptions && userOptions && forms.map((form, index) => (
                    <AddParticipantForm key={index} count={index} afterAddingParticipant={getData}
                        competitionOptions={competitionOptions} countryOptions={countryOptions} schoolOptions={schoolOptions}
                        userOptions={userOptions} forms={forms} setForms={setForms} form={form} />
                ))} */}
        {loadingTable && <Loader height={600} />}
        {!loadingTable &&
          participantTable &&
          makeSureIsArray(participantTable.data).length && (
            <HeavyTable
              handleSortingFunction={handleSortingFunction}
              sortableKeys={sortAbleColumns}
              headers={header}
              list={participantTable}
              title="participants"
              rowsPerPage={rowsPerPage}
              setPartcipient={setPartcipient}
              setOnDelete={setOnDelete}
              onChangePage={onChangePage}
              fixed={["name", "index_no", "country_name"]}
              selecting={selecting}
              setSelecting={setSelecting}
              selected={selected}
              setSelected={setSelected}
              setId={setId}
              setOnEdit={setOnEditParticipant}
            />
          )}
        {!loadingTable &&
          participantTable &&
          !makeSureIsArray(participantTable.data).length && (
            <NoData height={600} />
          )}
      </Grid>
      <Modal open={onDelete} onClose={() => cancelDelete()}>
        <>
          {Boolean(participantTable) && (
            <DeleteModal
              table={participantTable}
              id={id}
              selected={selected}
              deleteFunction={deleteFunction}
              cancelDelete={cancelDelete}
            />
          )}
        </>
      </Modal>
      <Modal open={onAddParticipant} onClose={() => setOnAddParticipant(false)}>
        <>
          <AddParticipantModal
            setLoading={setLoading}
            setOnAddParticipant={setOnAddParticipant}
            afterAddingParticipant={afterFunction}
          />
        </>
      </Modal>
      <Modal open={onEditParticipant} onClose={() => cancelEdit()}>
        <>
          {participantTable && (
            <EditParticipantModal
              setLoading={setLoading}
              table={participantTable}
              id={id}
              afterEditingParticipant={afterFunction}
              cancelEdit={cancelEdit}
            />
          )}
        </>
      </Modal>
      <Modal open={onExport} onClose={() => cancelExport()}>
        <>
          <ExportParticipantModal
            formatTableData={formatTableData}
            selected={selected}
            setSelected={setSelected}
            setSelecting={setSelecting}
            partData={participantTable}
            cancelExport={cancelExport}
          />
        </>
      </Modal>
    </Box>
  );
}
